// @ts-nocheck

import React, { useMemo } from 'react';
import { Box, Chip, Tooltip, Typography } from '@mui/material';
import TimelineIcon from '@mui/icons-material/Timeline';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import HourglassTopIcon from '@mui/icons-material/HourglassTop';
import ScheduleIcon from '@mui/icons-material/Schedule';
import BlockIcon from '@mui/icons-material/Block';
import { format, formatDistanceToNow } from 'date-fns';

import ModernSection from '../ModernSection/ModernSection';
import { useGetNextBatchRunQuery } from '../../../../gql/generated/graphql';

// ── Types ──────────────────────────────────────────────────────────────

interface CrawlStep {
  dbId: number;
  stepName: string;
  sequence?: number;
  startTime?: string;
  finishTime?: string;
  status?: string;
  success?: boolean;
  failed?: boolean;
  noOfAttempts?: number;
  errorMessage?: string;
}

interface CrawlRun {
  dbId: number;
  startedAt?: string;
  finishedAt?: string;
  status?: string;
  triggeredBy?: string;
  steps?: CrawlStep[];
}

interface CrawlProgressSectionProps {
  crawlRun?: CrawlRun | null;
  loading?: boolean;
}

// ── Pipeline definition ────────────────────────────────────────────────
// Each node has an id matching step_name, a human label, and edges to next nodes.
// `branch` marks decision points. Nodes can appear in multiple paths.

type PipelineNode = {
  id: string;
  label: string;
  next?: string[]; // ids of downstream nodes
  branch?: string; // label for a decision fork
  col?: number; // visual column (0 = center, -1 = left fork, 1 = right fork)
};

// The full pipeline graph. Order matters for rendering rows.
// Main sequence with two optional side-steps (social network, image recognition)
// that both feed back into the main line at "Calculate Company Size".
const PIPELINE: PipelineNode[] = [
  // Phase 1 – Classification gate
  { id: 'pre_classify_domain', label: 'Pre-classify Domain', next: ['crawl_the_domain'] },

  // Phase 2 – Core crawl
  { id: 'crawl_the_domain', label: 'Crawl Domain', next: ['post_crawl_classify_task'] },
  {
    id: 'post_crawl_classify_task',
    label: 'Post-crawl Classify',
    next: ['identify_all_images', 'extract_visual_pricing_task'],
    branch: 'Pricing analysis?',
  },
  {
    id: 'extract_visual_pricing_task',
    label: 'Pricing Analysis',
    col: 1,
  },
  { id: 'identify_all_images', label: 'Identify Images', next: ['sozial_network_analysis_callback_task'] },

  // Phase 3 – Social network + optional image recognition (SW companies)
  {
    id: 'sozial_network_analysis_callback_task',
    label: 'Social Network Analysis',
    next: ['submit_image_batch_if_needed', 'calculate_company_size'],
    branch: 'Images needed?',
  },
  {
    id: 'submit_image_batch_if_needed',
    label: 'Image Recognition',
    col: -1,
    next: ['calculate_company_size'],
  },

  // Phase 4 – Size estimation (all paths merge here)
  { id: 'calculate_company_size', label: 'Calculate Company Size', next: ['classify_all_pages_task'] },

  // Phase 5 – Analysis chain
  { id: 'classify_all_pages_task', label: 'Classify All Pages', next: ['check_openregistry_data_task'] },
  { id: 'check_openregistry_data_task', label: 'Check Open Registry', next: ['analyze_customer_references_task'] },
  {
    id: 'analyze_customer_references_task',
    label: 'Analyze Customer References',
    next: ['analyze_company_techstack_task'],
  },
  { id: 'analyze_company_techstack_task', label: 'Analyze Tech Stack', next: ['xovi_api_call_for_company_task'] },
  { id: 'xovi_api_call_for_company_task', label: 'XOVI API Call', next: ['classify_type_of_product_task'] },
  { id: 'classify_type_of_product_task', label: 'Classify Product Type', next: ['average_keywords_task'] },
  { id: 'average_keywords_task', label: 'Average Keywords', next: ['summarize_website_task'] },
  {
    id: 'summarize_website_task',
    label: 'Summarize Website',
    next: ['mongo_embedding_creation_for_single_company_task'],
  },
  {
    id: 'mongo_embedding_creation_for_single_company_task',
    label: 'Create Embeddings',
    next: ['collect_sourcescrub_data_task'],
  },

  // Phase 6 – Software extras + wrap-up
  { id: 'collect_sourcescrub_data_task', label: 'Collect SourceScrub', next: ['buy_and_build_list_prep_task'] },
  { id: 'buy_and_build_list_prep_task', label: 'Buy & Build Prep', next: ['synthesize_company_analysis_task'] },
  {
    id: 'synthesize_company_analysis_task',
    label: 'Synthesize Analysis',
    next: ['lazy_import_celery_task_calculate_company_kpis_task'],
  },
  { id: 'lazy_import_celery_task_calculate_company_kpis_task', label: 'Calculate KPIs', next: ['release_lock_task'] },

  // Final
  { id: 'release_lock_task', label: 'Release Lock' },
];

const ALL_STEP_IDS = new Set(PIPELINE.map(n => n.id));

// ── Helpers ────────────────────────────────────────────────────────────

type StepState = 'success' | 'failed' | 'inProgress' | 'notRun';

const COLORS: Record<StepState, { bg: string; border: string; text: string; dot: string }> = {
  success: {
    bg: 'var(--semantic-success-subtle, #e8f5e9)',
    border: 'var(--semantic-success, #4caf50)',
    text: 'var(--semantic-success-text, #2e7d32)',
    dot: 'var(--semantic-success, #4caf50)',
  },
  failed: {
    bg: 'var(--semantic-danger-subtle, #ffebee)',
    border: 'var(--semantic-danger, #f44336)',
    text: 'var(--semantic-danger-text, #c62828)',
    dot: 'var(--semantic-danger, #f44336)',
  },
  inProgress: {
    bg: 'var(--semantic-warning-subtle, #fff3e0)',
    border: 'var(--semantic-warning, #ff9800)',
    text: 'var(--semantic-warning-text, #e65100)',
    dot: 'var(--semantic-warning, #ff9800)',
  },
  notRun: {
    bg: 'var(--surface-raised, #fafafa)',
    border: 'var(--border-subtle, #e0e0e0)',
    text: 'var(--text-disabled, #bdbdbd)',
    dot: 'var(--border-subtle, #e0e0e0)',
  },
};

const getState = (step?: CrawlStep): StepState => {
  if (!step) return 'notRun';
  if (step.success) return 'success';
  if (step.failed) return 'failed';
  if (step.status === 'In Progress' || step.status === 'Retry in Progress') return 'inProgress';
  return 'notRun';
};

const formatTime = (d?: string) => {
  if (!d) return null;
  try {
    return format(new Date(d), 'MMM d, HH:mm:ss');
  } catch {
    return null;
  }
};

const formatStepLabel = (label: string) => label;

// ── Edge SVG ───────────────────────────────────────────────────────────

const NODE_W = 190;
const NODE_H = 36;
const ROW_GAP = 8;
const COL_OFFSET = 220; // horizontal offset for branched columns

// ── Components ─────────────────────────────────────────────────────────

const FlowNode = ({ node, step, executed }: { node: PipelineNode; step?: CrawlStep; executed: boolean }) => {
  const state = getState(step);
  const isActive = executed && state !== 'notRun';
  const c = isActive ? COLORS[state] : COLORS.notRun;

  const tooltip = step
    ? [
        step.startTime && `Started: ${formatTime(step.startTime)}`,
        step.finishTime && `Finished: ${formatTime(step.finishTime)}`,
        step.noOfAttempts && step.noOfAttempts > 1 && `Attempts: ${step.noOfAttempts}`,
        step.errorMessage && `Error: ${step.errorMessage}`,
      ]
        .filter(Boolean)
        .join('\n')
    : 'Not executed in this crawl';

  return (
    <Tooltip title={tooltip} placement="right" arrow>
      <Box
        sx={{
          width: NODE_W,
          height: NODE_H,
          px: 1.5,
          borderRadius: '6px',
          backgroundColor: c.bg,
          border: `1.5px solid ${c.border}`,
          display: 'flex',
          alignItems: 'center',
          gap: 0.75,
          cursor: 'default',
          opacity: isActive ? 1 : 0.5,
          transition: 'all 0.15s',
          '&:hover': { opacity: 1, boxShadow: `0 0 0 1px ${c.border}` },
          position: 'relative',
        }}
      >
        {/* Status dot */}
        <Box
          sx={{
            width: 10,
            height: 10,
            borderRadius: '50%',
            backgroundColor: c.dot,
            flexShrink: 0,
          }}
        />
        <Typography
          variant="caption"
          sx={{
            color: c.text,
            fontWeight: isActive ? 600 : 400,
            lineHeight: 1.2,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {formatStepLabel(node.label)}
        </Typography>
        {step?.noOfAttempts != null && step.noOfAttempts > 1 && (
          <Chip
            label={`${step.noOfAttempts}x`}
            size="small"
            color="warning"
            variant="outlined"
            sx={{ height: 18, fontSize: '0.65rem', ml: 'auto' }}
          />
        )}
      </Box>
    </Tooltip>
  );
};

// ── SVG connector drawing ──────────────────────────────────────────────

const ConnectorCanvas = ({
  pipeline,
  executedIds,
  nodePositions,
}: {
  pipeline: PipelineNode[];
  executedIds: Set<string>;
  nodePositions: Map<string, { x: number; y: number }>;
}) => {
  const lines: { x1: number; y1: number; x2: number; y2: number; active: boolean }[] = [];

  for (const node of pipeline) {
    if (!node.next) continue;
    const from = nodePositions.get(node.id);
    if (!from) continue;

    for (const nextId of node.next) {
      const to = nodePositions.get(nextId);
      if (!to) continue;

      const active = executedIds.has(node.id) && executedIds.has(nextId);
      lines.push({
        x1: from.x + NODE_W / 2,
        y1: from.y + NODE_H,
        x2: to.x + NODE_W / 2,
        y2: to.y,
        active,
      });
    }
  }

  if (lines.length === 0) return null;

  const allX = lines.flatMap(l => [l.x1, l.x2]);
  const allY = lines.flatMap(l => [l.y1, l.y2]);
  const maxX = Math.max(...allX) + 20;
  const maxY = Math.max(...allY) + 20;

  return (
    <svg style={{ position: 'absolute', top: 0, left: 0, width: maxX, height: maxY, pointerEvents: 'none' }}>
      {lines.map((l, i) => {
        const midY = (l.y1 + l.y2) / 2;
        const isStraight = Math.abs(l.x1 - l.x2) < 2;
        const d = isStraight
          ? `M${l.x1},${l.y1} L${l.x2},${l.y2}`
          : `M${l.x1},${l.y1} C${l.x1},${midY} ${l.x2},${midY} ${l.x2},${l.y2}`;

        return (
          <path
            key={i}
            d={d}
            fill="none"
            stroke={l.active ? 'var(--semantic-success, #4caf50)' : 'var(--border-subtle, #e0e0e0)'}
            strokeWidth={l.active ? 2 : 1.5}
            strokeDasharray={l.active ? undefined : '4 3'}
            opacity={l.active ? 0.8 : 0.4}
          />
        );
      })}
    </svg>
  );
};

// ── Main component ─────────────────────────────────────────────────────

const CrawlProgressSection: React.FC<CrawlProgressSectionProps> = ({ crawlRun, loading }) => {
  const { data: batchRunData } = useGetNextBatchRunQuery({ fetchPolicy: 'cache-and-network' });
  const batchRun = batchRunData?.getNextBatchRun;

  const steps = crawlRun?.steps || [];

  // Map step_name → CrawlStep for lookup. Also match steps not in our pipeline.
  const stepMap = useMemo(() => {
    const m = new Map<string, CrawlStep>();
    for (const s of steps) m.set(s.stepName, s);
    return m;
  }, [steps]);

  const executedIds = useMemo(() => new Set(steps.map(s => s.stepName)), [steps]);

  // Compute node positions for flow layout
  const nodePositions = useMemo(() => {
    const pos = new Map<string, { x: number; y: number }>();
    const centerX = COL_OFFSET;
    let y = 0;

    for (const node of PIPELINE) {
      const col = node.col ?? 0;
      const x = centerX + col * COL_OFFSET;
      pos.set(node.id, { x, y });
      y += NODE_H + ROW_GAP;
    }

    return pos;
  }, []);

  // Find extra steps that executed but aren't in our pipeline definition
  const extraSteps = useMemo(() => steps.filter(s => !ALL_STEP_IDS.has(s.stepName)), [steps]);

  if (!crawlRun && !loading && !batchRun) return null;

  const runColors =
    crawlRun?.status === 'completed'
      ? COLORS.success
      : crawlRun?.status === 'failed'
        ? COLORS.failed
        : COLORS.inProgress;
  const successCount = steps.filter(s => s.success).length;
  const failedCount = steps.filter(s => s.failed).length;
  const inProgressCount = steps.filter(
    s => !s.success && !s.failed && (s.status === 'In Progress' || s.status === 'Retry in Progress')
  ).length;

  // Total canvas height
  const canvasHeight = PIPELINE.length * (NODE_H + ROW_GAP);

  return (
    <ModernSection
      title="Crawl Progress"
      subtitle="Latest crawl execution timeline"
      icon={TimelineIcon}
      count={steps.length}
      loading={loading}
      collapsible
      defaultExpanded={false}
    >
      <Box>
        {/* Next Batch Run */}
        {batchRun && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              mb: 2,
              p: 1,
              borderRadius: 1,
              backgroundColor: batchRun.enabled
                ? 'var(--semantic-info-subtle, #f3f8ff)'
                : 'var(--semantic-danger-subtle, #fff5f5)',
              border: `1px solid ${batchRun.enabled ? 'var(--semantic-info-border, #bbdefb)' : 'var(--semantic-danger-border, #ffcdd2)'}`,
            }}
          >
            {batchRun.enabled ? (
              <ScheduleIcon sx={{ fontSize: 16, color: 'var(--semantic-info, #1976d2)' }} />
            ) : (
              <BlockIcon sx={{ fontSize: 16, color: 'var(--semantic-danger, #f44336)' }} />
            )}
            <Typography
              variant="caption"
              sx={{
                fontWeight: 600,
                color: batchRun.enabled ? 'var(--semantic-info-text, #1565c0)' : 'var(--semantic-danger-text, #c62828)',
              }}
            >
              {batchRun.scheduleLabel}:
            </Typography>
            {batchRun.enabled ? (
              <>
                <Typography variant="caption" color="text.secondary">
                  Next run{' '}
                  {batchRun.nextRunUtc && formatDistanceToNow(new Date(batchRun.nextRunUtc), { addSuffix: true })}
                </Typography>
                {batchRun.nextRunUtc && (
                  <Typography variant="caption" color="text.secondary" sx={{ opacity: 0.7 }}>
                    ({format(new Date(batchRun.nextRunUtc), 'MMM d, HH:mm')} UTC)
                  </Typography>
                )}
                <Tooltip title={`${batchRun.weekdaySchedule}\n${batchRun.weekendSchedule}`} arrow>
                  <Chip
                    label="Schedule"
                    size="small"
                    variant="outlined"
                    sx={{ height: 20, fontSize: '0.6rem', ml: 'auto', cursor: 'help' }}
                  />
                </Tooltip>
              </>
            ) : (
              <Chip
                label="DISABLED"
                size="small"
                sx={{
                  backgroundColor: 'var(--semantic-danger, #f44336)',
                  color: 'var(--text-on-emphasis, #fff)',
                  fontWeight: 600,
                  fontSize: '0.65rem',
                  height: 20,
                }}
              />
            )}
          </Box>
        )}

        {crawlRun ? (
          <>
            {/* Summary bar */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                mb: 2,
                p: 1.5,
                borderRadius: 1,
                backgroundColor: runColors.bg,
                border: `1px solid ${runColors.border}`,
                flexWrap: 'wrap',
              }}
            >
              <Chip
                label={crawlRun.status?.toUpperCase() || 'UNKNOWN'}
                size="small"
                sx={{
                  backgroundColor: runColors.dot,
                  color: 'var(--text-on-emphasis, #fff)',
                  fontWeight: 600,
                  fontSize: '0.7rem',
                }}
              />
              {crawlRun.startedAt && (
                <Typography variant="body2" color="text.secondary">
                  Started: {formatTime(crawlRun.startedAt)}
                </Typography>
              )}
              {crawlRun.finishedAt && (
                <Typography variant="body2" color="text.secondary">
                  Finished: {formatTime(crawlRun.finishedAt)}
                </Typography>
              )}
              {crawlRun.triggeredBy && <Chip label={crawlRun.triggeredBy} size="small" variant="outlined" />}
              <Box sx={{ ml: 'auto', display: 'flex', gap: 1 }}>
                {successCount > 0 && (
                  <Chip
                    icon={<CheckCircleIcon sx={{ fontSize: 14 }} />}
                    label={successCount}
                    size="small"
                    color="success"
                    variant="outlined"
                    sx={{ height: 24 }}
                  />
                )}
                {inProgressCount > 0 && (
                  <Chip
                    icon={<HourglassTopIcon sx={{ fontSize: 14 }} />}
                    label={inProgressCount}
                    size="small"
                    color="warning"
                    variant="outlined"
                    sx={{ height: 24 }}
                  />
                )}
                {failedCount > 0 && (
                  <Chip
                    icon={<ErrorIcon sx={{ fontSize: 14 }} />}
                    label={failedCount}
                    size="small"
                    color="error"
                    variant="outlined"
                    sx={{ height: 24 }}
                  />
                )}
              </Box>
            </Box>

            {/* Flow diagram */}
            <Box sx={{ position: 'relative', height: canvasHeight, overflow: 'auto' }}>
              <ConnectorCanvas pipeline={PIPELINE} executedIds={executedIds} nodePositions={nodePositions} />

              {PIPELINE.map(node => {
                const p = nodePositions.get(node.id);
                if (!p) return null;
                const step = stepMap.get(node.id);
                const executed = executedIds.has(node.id);

                return (
                  <Box key={node.id} sx={{ position: 'absolute', left: p.x, top: p.y }}>
                    <FlowNode node={node} step={step} executed={executed} />
                    {node.branch && (
                      <Typography
                        variant="caption"
                        sx={{
                          position: 'absolute',
                          right: -60,
                          top: '50%',
                          transform: 'translateY(-50%)',
                          color: 'var(--text-tertiary, #9e9e9e)',
                          fontStyle: 'italic',
                          fontSize: '0.65rem',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {node.branch}
                      </Typography>
                    )}
                  </Box>
                );
              })}
            </Box>

            {/* Extra steps not in pipeline definition */}
            {extraSteps.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                  Additional steps recorded:
                </Typography>
                <Box display="flex" flexWrap="wrap" gap={0.5}>
                  {extraSteps.map(s => {
                    const state = getState(s);
                    const c = COLORS[state];
                    return (
                      <Tooltip
                        key={s.dbId}
                        title={[
                          s.startTime && `Started: ${formatTime(s.startTime)}`,
                          s.finishTime && `Finished: ${formatTime(s.finishTime)}`,
                          s.errorMessage && `Error: ${s.errorMessage}`,
                        ]
                          .filter(Boolean)
                          .join('\n')}
                        arrow
                      >
                        <Chip
                          label={s.stepName.replace(/_task$/, '').replace(/_/g, ' ')}
                          size="small"
                          sx={{
                            backgroundColor: c.bg,
                            border: `1px solid ${c.border}`,
                            color: c.text,
                            fontSize: '0.7rem',
                            height: 24,
                          }}
                        />
                      </Tooltip>
                    );
                  })}
                </Box>
              </Box>
            )}
          </>
        ) : !batchRun ? (
          <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
            No crawl data available
          </Typography>
        ) : null}
      </Box>
    </ModernSection>
  );
};

export default CrawlProgressSection;
